import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { useAuth, API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { FileText, Loader2, Download, FileSpreadsheet, Pencil, Plus, Trash2, RefreshCw } from "lucide-react";

const fmtM = (n) => (n || 0).toLocaleString("es-DO", { minimumFractionDigits: 2 });
const TIPOS_BIENES = [["01", "01 - Gastos de personal"], ["02", "02 - Trabajos, suministros y servicios"], ["03", "03 - Arrendamientos"], ["04", "04 - Gastos de activos fijos"], ["05", "05 - Gastos de representación"], ["06", "06 - Otras deducciones admitidas"], ["07", "07 - Gastos financieros"], ["08", "08 - Gastos extraordinarios"], ["09", "09 - Compras que forman parte del costo de venta"], ["10", "10 - Adquisición de activos"], ["11", "11 - Gastos de seguros"]];
const FORMAS_PAGO = [["01", "01 - Efectivo"], ["02", "02 - Cheque/Transferencia/Depósito"], ["03", "03 - Tarjeta"], ["04", "04 - A crédito"], ["05", "05 - Permuta"], ["06", "06 - Nota de crédito"], ["07", "07 - Mixto"]];
const TIPOS_INGRESO = [["01", "01 - Ingresos por operaciones"], ["02", "02 - Ingresos financieros"], ["03", "03 - Ingresos extraordinarios"], ["04", "04 - Ingresos por arrendamientos"], ["05", "05 - Venta de activo depreciable"], ["06", "06 - Otros ingresos"]];
const TIPOS_ANULACION = [["01", "01 - Deterioro de factura pre-impresa"], ["02", "02 - Errores de impresión"], ["03", "03 - Impresión defectuosa"], ["04", "04 - Corrección de la información"], ["05", "05 - Cambio de productos"], ["06", "06 - Devolución de productos"], ["07", "07 - Omisión de productos"], ["08", "08 - Errores en secuencia de NCF"], ["09", "09 - Por cese de operaciones"], ["10", "10 - Pérdida o robo de talonarios"]];

const Sel = ({ value, onChange, opciones, testid }) => (
  <select className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    <option value="">—</option>
    {opciones.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

export default function DGIIReportesPage() {
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes((user?.role || "").toLowerCase());
  const puedeEditar = isAdmin || (user?.role || "").toLowerCase().includes("supervisor");
  const hoy = new Date();
  const [periodo, setPeriodo] = useState(`${hoy.getFullYear()}${String(hoy.getMonth() + 1).padStart(2, "0")}`);
  const [tipo, setTipo] = useState("606");
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rnc, setRnc] = useState("");
  const [edit, setEdit] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [nuevo608, setNuevo608] = useState({ ncf: "", fecha: "", tipo_anulacion: "04" });
  const [qbEstado, setQbEstado] = useState(null);

  const fetchRegs = useCallback(async (t = tipo, p = periodo) => {
    if (!/^\d{6}$/.test(p)) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dgii/registros?tipo=${t}&periodo=${p}`, getAuthHeaders());
      setRegs(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Error cargando registros"); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuthHeaders, tipo, periodo]);

  useEffect(() => {
    fetchRegs();
    axios.get(`${API}/dgii/config`, getAuthHeaders()).then((r) => setRnc(r.data.rnc || "")).catch(() => {});
    axios.get(`${API}/quickbooks/estado`, getAuthHeaders()).then((r) => setQbEstado(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncAhora = async () => {
    try {
      const r = await axios.post(`${API}/quickbooks/sync-now`, {}, getAuthHeaders());
      toast.success(r.data.message);
    } catch { toast.error("Error"); }
  };

  const guardarRnc = async () => {
    try {
      await axios.post(`${API}/dgii/config`, { rnc }, getAuthHeaders());
      toast.success("RNC de la empresa guardado");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const guardarEdit = async () => {
    try {
      setGuardando(true);
      const { txn_id, suplidor, cliente, fecha, total, ...campos } = edit;
      await axios.put(`${API}/dgii/registros/${tipo}/${txn_id}`, { periodo, campos }, getAuthHeaders());
      toast.success("Registro actualizado");
      setEdit(null);
      fetchRegs();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setGuardando(false); }
  };

  const exportar = async (formato) => {
    try {
      const res = await axios.get(`${API}/dgii/export/${formato}?tipo=${tipo}&periodo=${periodo}`, { ...getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `DGII_F_${tipo}_${rnc}_${periodo}.${formato === "txt" ? "TXT" : "xlsx"}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e.response?.data instanceof Blob ? await e.response.data.text() : "";
      toast.error(msg.includes("RNC") ? "Configure primero el RNC de la empresa" : "Error al exportar");
    }
  };

  const agregar608 = async () => {
    try {
      await axios.post(`${API}/dgii/608`, { ...nuevo608, periodo }, getAuthHeaders());
      toast.success("NCF anulado agregado");
      setNuevo608({ ncf: "", fecha: "", tipo_anulacion: "04" });
      fetchRegs();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const eliminar608 = async (id) => {
    try { await axios.delete(`${API}/dgii/608/${id}`, getAuthHeaders()); fetchRegs(); } catch { toast.error("Error"); }
  };

  const setE = (k, v) => setEdit((e) => ({ ...e, [k]: v }));
  const cambiarTab = (t) => { setTipo(t); fetchRegs(t, periodo); };
  const totalCol = (campo) => regs.filter((r) => !r.excluido).reduce((s, r) => s + (parseFloat(r[campo]) || 0), 0);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="dgii-page">
        <div className="mb-6 rounded-2xl bg-gradient-to-r from-[#0C314C] via-[#123f61] to-[#1a5a8a] p-6 shadow-lg">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center"><FileText className="w-6 h-6 text-white" /></span>
            Reportes DGII
          </h1>
          <p className="text-blue-100/90 mt-1.5">Formatos 606, 607 y 608 (Norma 07-18) con datos sincronizados desde QuickBooks</p>
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <Label className="text-xs">Período (AAAAMM)</Label>
            <Input className="w-32" value={periodo} onChange={(e) => setPeriodo(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="dgii-periodo" />
          </div>
          <Button variant="outline" onClick={() => fetchRegs()} data-testid="dgii-cargar-btn">Cargar</Button>
          {isAdmin && (
            <div className="flex items-end gap-2 ml-auto">
              <div>
                <Label className="text-xs">RNC de la empresa</Label>
                <Input className="w-40" value={rnc} onChange={(e) => setRnc(e.target.value)} placeholder="RNC" data-testid="dgii-rnc" />
              </div>
              <Button variant="outline" onClick={guardarRnc} data-testid="dgii-rnc-guardar">Guardar RNC</Button>
            </div>
          )}
        </div>

        {qbEstado && qbEstado.facturas_compra === 0 && qbEstado.facturas_venta === 0 && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5 text-sm text-amber-800 dark:text-amber-200 flex flex-wrap items-center justify-between gap-2" data-testid="dgii-alerta-sync">
            <span>
              Aún no hay facturas de compras/ventas sincronizadas desde QuickBooks.
              Presione <strong>Sincronizar ahora</strong> y luego ejecute <strong>Update Selected</strong> en el Web Connector (o espere la corrida automática); al terminar, vuelva a presionar Cargar.
              {qbEstado.ultima_sync_catalogos && <span className="block text-xs mt-0.5">Última sincronización de catálogos: {new Date(qbEstado.ultima_sync_catalogos).toLocaleString("es-DO")}</span>}
            </span>
            <Button size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100" onClick={syncAhora} data-testid="dgii-sync-now">
              <RefreshCw className="w-4 h-4 mr-1" />Sincronizar ahora
            </Button>
          </div>
        )}

        <Tabs value={tipo} onValueChange={cambiarTab}>
          <TabsList data-testid="dgii-tabs">
            <TabsTrigger value="606" data-testid="tab-606">606 — Compras</TabsTrigger>
            <TabsTrigger value="607" data-testid="tab-607">607 — Ventas</TabsTrigger>
            <TabsTrigger value="608" data-testid="tab-608">608 — NCF Anulados</TabsTrigger>
          </TabsList>

          {["606", "607", "608"].map((t) => {
            const activos = regs.filter((r) => !r.excluido);
            const suma = (campo) => activos.reduce((a, r) => a + (parseFloat(r[campo]) || 0), 0);
            const resumen = t === "606"
              ? [["Registros", activos.length, false],
                 ["Monto Facturado", suma("monto_servicios") + suma("monto_bienes"), true],
                 ["ITBIS Facturado", suma("itbis_facturado"), true],
                 ["Retenciones", suma("itbis_retenido") + suma("monto_retencion_renta"), true]]
              : t === "607"
                ? [["Registros", activos.length, false],
                   ["Monto Facturado", suma("monto_facturado"), true],
                   ["ITBIS Facturado", suma("itbis_facturado"), true]]
                : [["NCF Anulados", activos.length, false]];
            return (
            <TabsContent key={t} value={t} className="mt-4 space-y-3">
              {/* Resumen del período para cuadrar contra el IT-1 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`dgii-resumen-${t}`}>
                {resumen.map(([label, valor, esMonto]) => (
                  <Card key={label} className="shadow-sm border-l-4 border-l-[#0C314C]">
                    <CardContent className="p-3">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
                      <p className="text-lg font-bold text-[#0C314C] dark:text-white" data-testid={`dgii-resumen-${t}-${label.toLowerCase().replace(/ /g, "-")}`}>
                        {esMonto ? `RD$ ${fmtM(valor)}` : valor}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="flex flex-wrap justify-between items-center gap-2">
                <p className="text-sm text-slate-500">
                  {regs.filter((r) => !r.excluido).length} registros a reportar en {periodo}
                  {t !== "608" && (puedeEditar ? " (sincronizados de QuickBooks; use el lápiz para completar campos)" : " (sincronizados de QuickBooks)")}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={() => exportar("excel")} data-testid={`dgii-excel-${t}`}>
                    <FileSpreadsheet className="w-4 h-4 mr-1" />Excel
                  </Button>
                  <Button className="bg-[#0C314C] hover:bg-[#0a2940] text-white" onClick={() => exportar("txt")} data-testid={`dgii-txt-${t}`}>
                    <Download className="w-4 h-4 mr-1" />TXT Oficina Virtual
                  </Button>
                </div>
              </div>

              {t === "608" && (
                <Card className="shadow-sm"><CardContent className="p-3 flex flex-wrap items-end gap-2">
                  <div><Label className="text-xs">NCF anulado</Label><Input className="w-44" value={nuevo608.ncf} onChange={(e) => setNuevo608({ ...nuevo608, ncf: e.target.value })} data-testid="n608-ncf" /></div>
                  <div><Label className="text-xs">Fecha</Label><Input type="date" value={nuevo608.fecha} onChange={(e) => setNuevo608({ ...nuevo608, fecha: e.target.value })} data-testid="n608-fecha" /></div>
                  <div className="w-72"><Label className="text-xs">Tipo de anulación</Label><Sel value={nuevo608.tipo_anulacion} onChange={(v) => setNuevo608({ ...nuevo608, tipo_anulacion: v })} opciones={TIPOS_ANULACION} testid="n608-tipo" /></div>
                  <Button onClick={agregar608} disabled={!nuevo608.ncf || !nuevo608.fecha} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="n608-agregar"><Plus className="w-4 h-4 mr-1" />Agregar</Button>
                </CardContent></Card>
              )}

              {loading ? <div className="flex justify-center py-10"><Loader2 className="w-7 h-7 animate-spin text-slate-400" /></div> : (
                <Card className="shadow-md overflow-hidden"><CardContent className="p-0 overflow-x-auto max-h-[60vh] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 z-10"><tr className="bg-[#0C314C] text-left text-white">
                      {(t === "606" ? ["Fecha", "Suplidor", "RNC", "NCF", "Tipo B/S", "Servicios", "Bienes", "ITBIS", "Retenciones", "Forma Pago", ""]
                        : t === "607" ? ["Fecha", "Cliente", "RNC", "NCF", "Tipo Ingreso", "Monto", "ITBIS", "Formas de venta", ""]
                          : ["NCF", "Fecha", "Tipo Anulación", ""]).map((h, i) => <th key={i} className="p-2.5 font-semibold whitespace-nowrap text-center">{h}</th>)}
                    </tr></thead>
                    <tbody data-testid={`dgii-body-${t}`}>
                      {regs.length === 0 ? (
                        <tr><td colSpan={11} className="p-8 text-center text-slate-400">
                          {t === "608" ? "No hay NCF anulados en este período" : "No hay facturas sincronizadas de QuickBooks para este período (verifique la sincronización del Web Connector)"}
                        </td></tr>
                      ) : regs.map((r, i) => (
                        <tr key={r.txn_id || r.id} className={`border-b ${r.excluido ? "opacity-40 line-through" : ""} ${i % 2 === 1 ? "bg-slate-50/70 dark:bg-slate-800/30" : ""}`} data-testid={`dgii-row-${r.txn_id || r.id}`}>
                          {t === "606" && (<>
                            <td className="p-2 whitespace-nowrap text-center">{r.fecha}</td>
                            <td className="p-2 font-medium dark:text-white text-center">{r.suplidor}</td>
                            <td className="p-2 font-mono text-center">{r.rnc || <Badge className="bg-red-100 text-red-700">falta</Badge>}</td>
                            <td className="p-2 font-mono text-center">{r.ncf || <Badge className="bg-red-100 text-red-700">falta</Badge>}</td>
                            <td className="p-2 text-center">{r.tipo_bienes}</td>
                            <td className="p-2 text-center">{fmtM(r.monto_servicios)}</td>
                            <td className="p-2 text-center">{fmtM(r.monto_bienes)}</td>
                            <td className="p-2 text-center">{fmtM(r.itbis_facturado)}</td>
                            <td className="p-2 text-center">{fmtM((parseFloat(r.itbis_retenido) || 0) + (parseFloat(r.monto_retencion_renta) || 0))}</td>
                            <td className="p-2 text-center">{r.forma_pago}</td>
                          </>)}
                          {t === "607" && (<>
                            <td className="p-2 whitespace-nowrap text-center">{r.fecha}</td>
                            <td className="p-2 font-medium dark:text-white text-center">{r.cliente}</td>
                            <td className="p-2 font-mono text-center">{r.rnc || <Badge className="bg-red-100 text-red-700">falta</Badge>}</td>
                            <td className="p-2 font-mono text-center">{r.ncf || <Badge className="bg-red-100 text-red-700">falta</Badge>}</td>
                            <td className="p-2 text-center">{r.tipo_ingreso}</td>
                            <td className="p-2 text-center">{fmtM(r.monto_facturado)}</td>
                            <td className="p-2 text-center">{fmtM(r.itbis_facturado)}</td>
                            <td className="p-2 text-center text-slate-500">{fmtM((parseFloat(r.efectivo) || 0) + (parseFloat(r.cheque_transferencia) || 0) + (parseFloat(r.tarjeta) || 0) + (parseFloat(r.credito) || 0) + (parseFloat(r.bonos) || 0) + (parseFloat(r.permuta) || 0) + (parseFloat(r.otras_formas) || 0))}</td>
                          </>)}
                          {t === "608" && (<>
                            <td className="p-2 font-mono text-center">{r.ncf}</td>
                            <td className="p-2 text-center">{r.fecha}</td>
                            <td className="p-2 text-center">{TIPOS_ANULACION.find(([v]) => v === r.tipo_anulacion)?.[1] || r.tipo_anulacion}</td>
                          </>)}
                          <td className="p-2 text-center">
                            {t === "608" ? (
                              <Button size="sm" variant="outline" className="h-7 px-2 text-red-600" onClick={() => eliminar608(r.id)} data-testid={`del608-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                            ) : puedeEditar ? (
                              <Button size="sm" variant="outline" className="h-7 px-2 text-indigo-600" onClick={() => setEdit({ ...r })} data-testid={`edit-${r.txn_id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {t !== "608" && regs.length > 0 && (
                      <tfoot><tr className="bg-slate-100 dark:bg-slate-800 font-semibold">
                        <td className="p-2" colSpan={t === "606" ? 5 : 5}>TOTALES</td>
                        {t === "606" ? (<>
                          <td className="p-2 text-center">{fmtM(totalCol("monto_servicios"))}</td>
                          <td className="p-2 text-center">{fmtM(totalCol("monto_bienes"))}</td>
                          <td className="p-2 text-center">{fmtM(totalCol("itbis_facturado"))}</td>
                          <td className="p-2" colSpan={3}></td>
                        </>) : (<>
                          <td className="p-2 text-center">{fmtM(totalCol("monto_facturado"))}</td>
                          <td className="p-2 text-center">{fmtM(totalCol("itbis_facturado"))}</td>
                          <td className="p-2" colSpan={2}></td>
                        </>)}
                      </tr></tfoot>
                    )}
                  </table>
                </CardContent></Card>
              )}
            </TabsContent>
            );
          })}
        </Tabs>

        {/* ===== Dialog editar registro 606/607 ===== */}
        <Dialog open={!!edit} onOpenChange={(v) => { if (!v) setEdit(null); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dgii-edit-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-[#0C314C]" />Completar registro {tipo} — {edit?.suplidor || edit?.cliente}</DialogTitle></DialogHeader>
            {edit && (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><Label>RNC/Cédula</Label><Input value={edit.rnc} onChange={(e) => setE("rnc", e.target.value)} data-testid="e-rnc" /></div>
                <div><Label>NCF</Label><Input value={edit.ncf} onChange={(e) => setE("ncf", e.target.value)} data-testid="e-ncf" /></div>
                <div><Label>NCF Modificado</Label><Input value={edit.ncf_modificado} onChange={(e) => setE("ncf_modificado", e.target.value)} /></div>
                {tipo === "606" ? (<>
                  <div><Label>Tipo Bienes/Servicios</Label><Sel value={edit.tipo_bienes} onChange={(v) => setE("tipo_bienes", v)} opciones={TIPOS_BIENES} testid="e-tipobs" /></div>
                  <div><Label>Fecha Pago</Label><Input type="date" value={edit.fecha_pago} onChange={(e) => setE("fecha_pago", e.target.value)} /></div>
                  <div><Label>Monto Servicios</Label><Input type="number" step="0.01" value={edit.monto_servicios} onChange={(e) => setE("monto_servicios", e.target.value)} /></div>
                  <div><Label>Monto Bienes</Label><Input type="number" step="0.01" value={edit.monto_bienes} onChange={(e) => setE("monto_bienes", e.target.value)} /></div>
                  <div><Label>ITBIS Facturado</Label><Input type="number" step="0.01" value={edit.itbis_facturado} onChange={(e) => setE("itbis_facturado", e.target.value)} data-testid="e-itbis" /></div>
                  <div><Label>ITBIS Retenido</Label><Input type="number" step="0.01" value={edit.itbis_retenido} onChange={(e) => setE("itbis_retenido", e.target.value)} /></div>
                  <div><Label>ITBIS por Adelantar</Label><Input type="number" step="0.01" value={edit.itbis_adelantar} onChange={(e) => setE("itbis_adelantar", e.target.value)} /></div>
                  <div><Label>ITBIS llevado al Costo</Label><Input type="number" step="0.01" value={edit.itbis_costo} onChange={(e) => setE("itbis_costo", e.target.value)} /></div>
                  <div><Label>Tipo Retención ISR (1-8)</Label><Input value={edit.tipo_retencion_isr} onChange={(e) => setE("tipo_retencion_isr", e.target.value)} /></div>
                  <div><Label>Monto Retención Renta</Label><Input type="number" step="0.01" value={edit.monto_retencion_renta} onChange={(e) => setE("monto_retencion_renta", e.target.value)} /></div>
                  <div><Label>Impuesto Selectivo</Label><Input type="number" step="0.01" value={edit.impuesto_selectivo} onChange={(e) => setE("impuesto_selectivo", e.target.value)} /></div>
                  <div><Label>Otros Impuestos</Label><Input type="number" step="0.01" value={edit.otros_impuestos} onChange={(e) => setE("otros_impuestos", e.target.value)} /></div>
                  <div><Label>Propina Legal</Label><Input type="number" step="0.01" value={edit.propina_legal} onChange={(e) => setE("propina_legal", e.target.value)} /></div>
                  <div><Label>Forma de Pago</Label><Sel value={edit.forma_pago} onChange={(v) => setE("forma_pago", v)} opciones={FORMAS_PAGO} testid="e-formapago" /></div>
                </>) : (<>
                  <div><Label>Tipo de Ingreso</Label><Sel value={edit.tipo_ingreso} onChange={(v) => setE("tipo_ingreso", v)} opciones={TIPOS_INGRESO} /></div>
                  <div><Label>Fecha Retención</Label><Input type="date" value={edit.fecha_retencion} onChange={(e) => setE("fecha_retencion", e.target.value)} /></div>
                  <div><Label>Monto Facturado</Label><Input type="number" step="0.01" value={edit.monto_facturado} onChange={(e) => setE("monto_facturado", e.target.value)} /></div>
                  <div><Label>ITBIS Facturado</Label><Input type="number" step="0.01" value={edit.itbis_facturado} onChange={(e) => setE("itbis_facturado", e.target.value)} /></div>
                  <div><Label>ITBIS Retenido por Terceros</Label><Input type="number" step="0.01" value={edit.itbis_retenido} onChange={(e) => setE("itbis_retenido", e.target.value)} /></div>
                  <div><Label>Retención Renta por Terceros</Label><Input type="number" step="0.01" value={edit.retencion_renta} onChange={(e) => setE("retencion_renta", e.target.value)} /></div>
                  <div><Label>Efectivo</Label><Input type="number" step="0.01" value={edit.efectivo} onChange={(e) => setE("efectivo", e.target.value)} /></div>
                  <div><Label>Cheque/Transferencia</Label><Input type="number" step="0.01" value={edit.cheque_transferencia} onChange={(e) => setE("cheque_transferencia", e.target.value)} /></div>
                  <div><Label>Tarjeta</Label><Input type="number" step="0.01" value={edit.tarjeta} onChange={(e) => setE("tarjeta", e.target.value)} /></div>
                  <div><Label>Venta a Crédito</Label><Input type="number" step="0.01" value={edit.credito} onChange={(e) => setE("credito", e.target.value)} /></div>
                  <div><Label>Bonos</Label><Input type="number" step="0.01" value={edit.bonos} onChange={(e) => setE("bonos", e.target.value)} /></div>
                  <div><Label>Permuta / Otras</Label><Input type="number" step="0.01" value={edit.permuta} onChange={(e) => setE("permuta", e.target.value)} /></div>
                </>)}
                <label className="flex items-center gap-2 col-span-2 cursor-pointer">
                  <input type="checkbox" checked={!!edit.excluido} onChange={(e) => setE("excluido", e.target.checked)} data-testid="e-excluido" />
                  Excluir este registro del reporte
                </label>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEdit(null)}>Cancelar</Button>
              <Button onClick={guardarEdit} disabled={guardando} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="e-guardar">
                {guardando && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
